import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { ChevronDown, Check } from "lucide-react";
import {
    createBCPPlan,
    updateBCPPlan,
    getBCPPlanById,
    getUserDirectory,
} from "../../../service/api";
import { useRoleManageStore, usePremisesStore } from "../../../store/store";
import axiosInstance from "../../../common/axiosInstance";


interface UserWithRole {
    id: string;
    name: string;
    employeeId: string;
    roleId: string;
    roleName: string;
    premise: string[];
}

const AddEditBCPPlan: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const planId = searchParams.get("id");
    const mode = searchParams.get("mode") || "create";
    const isViewMode = mode === "view";
    const isEditMode = mode === "edit";

    const { rolesData, fetchAllRoles } = useRoleManageStore();
    const { allPremises, fetchPremises } = usePremisesStore();

    const [formData, setFormData] = useState({
        planName: "",
        description: "",
        assignedUserId: "",
        assignedRoleId: "",
        status: "Draft",
        rto: 0,
        rpo: 0,
    });

    const [criticalFunctions, setCriticalFunctions] = useState<
        Array<{ functionName: string; priority: string; description: string }>
    >([]);
    const [alternateSites, setAlternateSites] = useState<
        Array<{
            siteName: string;
            siteType: string;
            address: string;
            capacity: number;
        }>
    >([]);
    const [itSystems, setItSystems] = useState<
        Array<{
            systemName: string;
            criticality: string;
            backupLocation: string;
            recoveryProcedure: string;
        }>
    >([]);

    const [loading, setLoading] = useState(false);
    const [selectedPremises, setSelectedPremises] = useState<string[]>([]);
    const [premisesDropdownOpen, setPremisesDropdownOpen] = useState(false);
    const [users, setUsers] = useState<UserWithRole[]>([]);
    const [filteredUsers, setFilteredUsers] = useState<UserWithRole[]>([]);

    // File upload state
    const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
    const [isDragging, setIsDragging] = useState(false);

    useEffect(() => {
        fetchAllRoles();
        fetchPremises();
        fetchUsersData();
        if (planId && (isEditMode || isViewMode)) {
            fetchPlanData();
        }
    }, [planId]);

    // Fetch users and their roles
    const fetchUsersData = async () => {
        try {
            const response = await getUserDirectory();
            const usersData = Array.isArray(response.data.data)
                ? response.data.data
                : [];

            // Filter orgusers only
            const orgUsers = usersData.filter(
                (user) => user.roles?.includes("orguser") && !user.isDeleted
            );

            // Fetch role names for each user
            const usersWithRoles = await Promise.all(
                orgUsers.map(async (user) => {
                    const roleId =
                        typeof user.role === "string" ? user.role : user.role?.$oid || "";
                    let roleName = "Unknown";

                    if (roleId) {
                        try {
                            const roleResponse = await axiosInstance.get(`/api/roles/${roleId}`);
                            roleName = roleResponse.data.message.roleName || "Unknown";
                        } catch (error) {
                            console.error(`Failed to fetch role for user ${user._id}:`, error);
                        }
                    }

                    // Normalize premise to string[]
                    let premiseArr: string[] = [];
                    if (Array.isArray(user.premise)) {
                        premiseArr = user.premise.map((p: any) =>
                            typeof p === "string" ? p : p.$oid
                        );
                    } else if (typeof user.premise === "string") {
                        premiseArr = [user.premise];
                    }

                    return {
                        id: user._id,
                        name: `${user.firstName || ""} ${user.lastName || ""}`.trim() || "Unknown",
                        employeeId: user.employeeId || "",
                        roleId,
                        roleName,
                        premise: premiseArr,
                    };
                })
            );

            setUsers(usersWithRoles);
            setFilteredUsers(usersWithRoles);
        } catch (error) {
            console.error("Error fetching users:", error);
            toast.error("Failed to fetch users");
        }
    };

    // Filter users by selected premises
    useEffect(() => {
        if (selectedPremises.length === 0 || selectedPremises.includes("ALL")) {
            setFilteredUsers(users);
        } else {
            setFilteredUsers(
                users.filter((user) =>
                    user.premise && user.premise.some((premId) =>
                        selectedPremises.includes(premId)
                    )
                )
            );
        }
    }, [selectedPremises, users]);

    // Handle click outside for premises dropdown
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            const dropdown = document.getElementById("premises-dropdown-menu");
            const button = document.getElementById("premises-dropdown-btn");
            if (
                dropdown &&
                !dropdown.contains(event.target as Node) &&
                button &&
                !button.contains(event.target as Node)
            ) {
                setPremisesDropdownOpen(false);
            }
        }
        if (premisesDropdownOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [premisesDropdownOpen]);

    const fetchPlanData = async () => {
        try {
            const response = await getBCPPlanById(planId!);
            if (response.data?.resCode === 200 && response.data?.data) {
                const plan = response.data.data;
                setFormData({
                    planName: plan.planName || "",
                    description: plan.description || "",
                    assignedUserId: plan.assignedUserId || "",
                    assignedRoleId: plan.assignedRoleId || "",
                    status: plan.status || "Draft",
                    rto: plan.recoveryObjectives?.rto || 0,
                    rpo: plan.recoveryObjectives?.rpo || 0,
                });
                setCriticalFunctions(plan.criticalFunctions || []);
                setAlternateSites(plan.alternateSites || []);
                setItSystems(plan.itSystems || []);
            }
        } catch (error: any) {
            console.error("Error fetching BCP Plan:", error);
            toast.error(
                error.response?.data?.resMessage || "Failed to fetch BCP Plan"
            );
        }
    };

    const handleInputChange = (
        e: React.ChangeEvent<
            HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >
    ) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleAddCriticalFunction = () => {
        setCriticalFunctions([
            ...criticalFunctions,
            { functionName: "", priority: "Medium", description: "" },
        ]);
    };

    const handleRemoveCriticalFunction = (index: number) => {
        setCriticalFunctions(criticalFunctions.filter((_, i) => i !== index));
    };

    const handleCriticalFunctionChange = (
        index: number,
        field: string,
        value: string
    ) => {
        const updated = [...criticalFunctions];
        updated[index] = { ...updated[index], [field]: value };
        setCriticalFunctions(updated);
    };

    const handleAddAlternateSite = () => {
        setAlternateSites([
            ...alternateSites,
            { siteName: "", siteType: "Hot", address: "", capacity: 0 },
        ]);
    };

    const handleRemoveAlternateSite = (index: number) => {
        setAlternateSites(alternateSites.filter((_, i) => i !== index));
    };

    const handleAlternateSiteChange = (
        index: number,
        field: string,
        value: string | number
    ) => {
        const updated = [...alternateSites];
        updated[index] = { ...updated[index], [field]: value };
        setAlternateSites(updated);
    };

    const handleAddITSystem = () => {
        setItSystems([
            ...itSystems,
            {
                systemName: "",
                criticality: "Medium",
                backupLocation: "",
                recoveryProcedure: "",
            },
        ]);
    };

    const handleRemoveITSystem = (index: number) => {
        setItSystems(itSystems.filter((_, i) => i !== index));
    };

    const handleITSystemChange = (
        index: number,
        field: string,
        value: string
    ) => {
        const updated = [...itSystems];
        updated[index] = { ...updated[index], [field]: value };
        setItSystems(updated);
    };

    // File upload handlers
    const validateFile = (file: File): boolean => {
        const allowedTypes = [
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'text/plain',
            'image/jpeg',
            'image/png',
        ];
        const maxSize = 5 * 1024 * 1024; // 5 MB

        if (!allowedTypes.includes(file.type)) {
            toast.error(`File type not supported: ${file.name}. Allowed: DOC, DOCX, PDF, TXT, JPG, PNG`);
            return false;
        }

        if (file.size > maxSize) {
            toast.error(`File too large: ${file.name}. Max size: 5 MB`);
            return false;
        }

        return true;
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        const validFiles = files.filter(validateFile);
        setUploadedFiles((prev) => [...prev, ...validFiles]);
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(false);
        const files = Array.from(e.dataTransfer.files);
        const validFiles = files.filter(validateFile);
        setUploadedFiles((prev) => [...prev, ...validFiles]);
    };

    const handleRemoveFile = (index: number) => {
        setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    const getFileIcon = (fileName: string): string => {
        const ext = fileName.split('.').pop()?.toLowerCase();
        switch (ext) {
            case 'pdf':
                return '📄';
            case 'doc':
            case 'docx':
                return '📝';
            case 'txt':
                return '📃';
            case 'jpg':
            case 'jpeg':
            case 'png':
                return '🖼️';
            default:
                return '📎';
        }
    };


    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.planName || !formData.assignedUserId) {
            toast.error("Plan name and assigned person are required");
            return;
        }

        setLoading(true);
        try {
            // Create FormData for file upload
            const formDataToSend = new FormData();

            // Append basic form data
            formDataToSend.append("planName", formData.planName);
            formDataToSend.append("description", formData.description);
            formDataToSend.append("assignedUserId", formData.assignedUserId);
            formDataToSend.append("assignedRoleId", formData.assignedRoleId);
            formDataToSend.append("status", formData.status);

            // Append recovery objectives
            formDataToSend.append("rto", String(formData.rto));
            formDataToSend.append("rpo", String(formData.rpo));

            // Append arrays as JSON strings
            formDataToSend.append("criticalFunctions", JSON.stringify(criticalFunctions));
            formDataToSend.append("alternateSites", JSON.stringify(alternateSites));
            formDataToSend.append("itSystems", JSON.stringify(itSystems));

            // Append files
            uploadedFiles.forEach((file, index) => {
                formDataToSend.append(`files`, file);
            });

            let response;
            if (isEditMode && planId) {
                response = await updateBCPPlan(planId, formDataToSend);
            } else {
                response = await createBCPPlan(formDataToSend);
            }

            if (response.data?.resCode === 200 || response.data?.resCode === 201) {
                toast.success(
                    response.data?.resMessage ||
                    `BCP Plan ${isEditMode ? "updated" : "created"} successfully`
                );
                navigate("/external-emergency-preparedness/bcp-plans");
            } else {
                throw new Error(
                    response.data?.resMessage ||
                    `Failed to ${isEditMode ? "update" : "create"} BCP Plan`
                );
            }
        } catch (error: any) {
            console.error("Error saving BCP Plan:", error);
            toast.error(
                error.response?.data?.resMessage ||
                error.message ||
                `Failed to ${isEditMode ? "update" : "create"} BCP Plan`
            );
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        navigate("/external-emergency-preparedness/bcp-plans");
    };

    return (
        <div className="px-2 py-4 mt-14 w-full">
            <div className="bg-white rounded-lg shadow-lg p-6">
                <h1 className="text-2xl font-semibold text-gray-900 mb-6">
                    {isViewMode
                        ? "View BCP Plan"
                        : isEditMode
                            ? "Edit BCP Plan"
                            : "Add BCP Plan"}
                </h1>

                <form onSubmit={handleSubmit}>
                    {/* Basic Information */}
                    <div className="mb-6">
                        <h2 className="text-lg font-semibold text-gray-800 mb-4">
                            Basic Information
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Plan Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="planName"
                                    value={formData.planName}
                                    onChange={handleInputChange}
                                    disabled={isViewMode}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Status
                                </label>
                                <select
                                    name="status"
                                    value={formData.status}
                                    onChange={handleInputChange}
                                    disabled={isViewMode}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
                                >
                                    <option value="Draft">Draft</option>
                                    <option value="Active">Active</option>
                                    <option value="Archived">Archived</option>
                                </select>
                            </div>

                            {/* Premises Multi-Select Dropdown */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Premises
                                </label>
                                <div className="relative">
                                    <button
                                        type="button"
                                        id="premises-dropdown-btn"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white flex justify-between items-center disabled:bg-gray-100"
                                        onClick={() => setPremisesDropdownOpen(!premisesDropdownOpen)}
                                        disabled={isViewMode}
                                    >
                                        <span className="truncate text-left flex-1">
                                            {selectedPremises.length === 0 || selectedPremises.includes("ALL")
                                                ? "All Premises"
                                                : selectedPremises.length === 1
                                                    ? allPremises.find((p: any) => p.id === selectedPremises[0])?.premisesName || "1 selected"
                                                    : `${selectedPremises.length} selected`}
                                        </span>
                                        <ChevronDown className="w-4 h-4 text-gray-500 ml-2" />
                                    </button>
                                    {premisesDropdownOpen && (
                                        <div
                                            id="premises-dropdown-menu"
                                            className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded shadow-lg max-h-60 overflow-y-auto"
                                        >
                                            <div
                                                className="px-2 py-1 hover:bg-gray-100 cursor-pointer flex items-center"
                                                onClick={() => setSelectedPremises(["ALL"])}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={selectedPremises.includes("ALL")}
                                                    readOnly
                                                    className="mr-2"
                                                />
                                                <span>All Premises</span>
                                                {selectedPremises.includes("ALL") && (
                                                    <Check className="w-4 h-4 text-green-500 ml-auto" />
                                                )}
                                            </div>
                                            {allPremises.map((premise: any) => (
                                                <div
                                                    key={premise.id || premise._id}
                                                    className="px-2 py-1 hover:bg-gray-100 cursor-pointer flex items-center"
                                                    onClick={() => {
                                                        let newSelected = [...selectedPremises];
                                                        if (selectedPremises.includes("ALL"))
                                                            newSelected = [];
                                                        if (newSelected.includes(premise.id)) {
                                                            newSelected = newSelected.filter((p) => p !== premise.id);
                                                        } else {
                                                            newSelected.push(premise.id);
                                                        }
                                                        setSelectedPremises(newSelected);
                                                    }}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={
                                                            selectedPremises.includes("ALL")
                                                                ? false
                                                                : selectedPremises.includes(premise.id)
                                                        }
                                                        readOnly
                                                        className="mr-2"
                                                    />
                                                    <span>{premise.premisesName}</span>
                                                    {selectedPremises.includes(premise.id) &&
                                                        !selectedPremises.includes("ALL") && (
                                                            <Check className="w-4 h-4 text-green-500 ml-auto" />
                                                        )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Assigned Person (User with Role) */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Assigned Person <span className="text-red-500">*</span>
                                </label>
                                <select
                                    name="assignedUserId"
                                    value={formData.assignedUserId}
                                    onChange={(e) => {
                                        const selectedUser = filteredUsers.find(u => u.id === e.target.value);
                                        setFormData({
                                            ...formData,
                                            assignedUserId: e.target.value,
                                            assignedRoleId: selectedUser?.roleId || "",
                                        });
                                    }}
                                    disabled={isViewMode}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
                                    required
                                >
                                    <option value="">Select Person</option>
                                    {filteredUsers.map((user) => (
                                        <option key={user.id} value={user.id}>
                                            {user.name} ({user.roleName}) - {user.employeeId}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Description
                                </label>
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    disabled={isViewMode}
                                    rows={3}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
                                />
                            </div>
                        </div>
                    </div>

                    {/* File Upload Section */}
                    <div className="mb-6">
                        <h2 className="text-lg font-semibold text-gray-800 mb-4">
                            Supporting Documents
                        </h2>
                        <div className="grid grid-cols-1 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Upload Files (Optional)
                                </label>
                                <div
                                    className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${isDragging
                                        ? "border-teal-500 bg-teal-50"
                                        : "border-gray-300 bg-gray-50"
                                        } ${isViewMode ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                                    onDragOver={!isViewMode ? handleDragOver : undefined}
                                    onDragLeave={!isViewMode ? handleDragLeave : undefined}
                                    onDrop={!isViewMode ? handleDrop : undefined}
                                    onClick={() => !isViewMode && document.getElementById("file-upload")?.click()}
                                >
                                    <div className="flex flex-col items-center">
                                        <svg
                                            className="w-12 h-12 text-gray-400 mb-3"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                                            />
                                        </svg>
                                        <p className="text-sm text-gray-600 mb-1">
                                            <span className="font-semibold text-teal-600">Click to upload</span> or drag and drop
                                        </p>
                                        <p className="text-xs text-gray-500">
                                            DOC, DOCX, PDF, TXT, JPG, PNG (Max 5 MB per file)
                                        </p>
                                    </div>
                                    <input
                                        id="file-upload"
                                        type="file"
                                        multiple
                                        accept=".doc,.docx,.pdf,.txt,.jpg,.jpeg,.png"
                                        onChange={handleFileSelect}
                                        disabled={isViewMode}
                                        className="hidden"
                                    />
                                </div>

                                {/* Uploaded Files List */}
                                {uploadedFiles.length > 0 && (
                                    <div className="mt-4 space-y-2">
                                        <p className="text-sm font-medium text-gray-700">Uploaded Files:</p>
                                        {uploadedFiles.map((file, index) => (
                                            <div
                                                key={index}
                                                className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg"
                                            >
                                                <div className="flex items-center gap-3 flex-1">
                                                    <span className="text-2xl">{getFileIcon(file.name)}</span>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-medium text-gray-900 truncate">
                                                            {file.name}
                                                        </p>
                                                        <p className="text-xs text-gray-500">
                                                            {formatFileSize(file.size)}
                                                        </p>
                                                    </div>
                                                </div>
                                                {!isViewMode && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveFile(index)}
                                                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                                                        title="Remove file"
                                                    >
                                                        <svg
                                                            className="w-5 h-5"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={2}
                                                                d="M6 18L18 6M6 6l12 12"
                                                            />
                                                        </svg>
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Recovery Objectives */}
                    <div className="mb-6">
                        <h2 className="text-lg font-semibold text-gray-800 mb-4">
                            Recovery Objectives
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    RTO (Recovery Time Objective - Hours)
                                </label>
                                <input
                                    type="number"
                                    name="rto"
                                    value={formData.rto}
                                    onChange={handleInputChange}
                                    disabled={isViewMode}
                                    min="0"
                                    step="0.5"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    RPO (Recovery Point Objective - Hours)
                                </label>
                                <input
                                    type="number"
                                    name="rpo"
                                    value={formData.rpo}
                                    onChange={handleInputChange}
                                    disabled={isViewMode}
                                    min="0"
                                    step="0.5"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Critical Functions */}
                    <div className="mb-6">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-lg font-semibold text-gray-800">
                                Critical Functions
                            </h2>
                            {!isViewMode && (
                                <button
                                    type="button"
                                    onClick={handleAddCriticalFunction}
                                    className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600"
                                >
                                    + Add Function
                                </button>
                            )}
                        </div>
                        {criticalFunctions.length === 0 ? (
                            <p className="text-gray-500 text-sm">
                                No critical functions added yet.
                            </p>
                        ) : (
                            <div className="space-y-4">
                                {criticalFunctions.map((func, index) => (
                                    <div
                                        key={index}
                                        className="border border-gray-300 rounded-lg p-4"
                                    >
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Function Name
                                                </label>
                                                <input
                                                    type="text"
                                                    value={func.functionName}
                                                    onChange={(e) =>
                                                        handleCriticalFunctionChange(
                                                            index,
                                                            "functionName",
                                                            e.target.value
                                                        )
                                                    }
                                                    disabled={isViewMode}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Priority
                                                </label>
                                                <select
                                                    value={func.priority}
                                                    onChange={(e) =>
                                                        handleCriticalFunctionChange(
                                                            index,
                                                            "priority",
                                                            e.target.value
                                                        )
                                                    }
                                                    disabled={isViewMode}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
                                                >
                                                    <option value="Critical">Critical</option>
                                                    <option value="High">High</option>
                                                    <option value="Medium">Medium</option>
                                                    <option value="Low">Low</option>
                                                </select>
                                            </div>
                                            <div className="md:col-span-3">
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Description
                                                </label>
                                                <textarea
                                                    value={func.description}
                                                    onChange={(e) =>
                                                        handleCriticalFunctionChange(
                                                            index,
                                                            "description",
                                                            e.target.value
                                                        )
                                                    }
                                                    disabled={isViewMode}
                                                    rows={2}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
                                                />
                                            </div>
                                        </div>
                                        {!isViewMode && (
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveCriticalFunction(index)}
                                                className="mt-2 text-red-500 hover:text-red-700 text-sm"
                                            >
                                                Remove
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Alternate Sites */}
                    <div className="mb-6">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-lg font-semibold text-gray-800">
                                Alternate Sites
                            </h2>
                            {!isViewMode && (
                                <button
                                    type="button"
                                    onClick={handleAddAlternateSite}
                                    className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600"
                                >
                                    + Add Site
                                </button>
                            )}
                        </div>
                        {alternateSites.length === 0 ? (
                            <p className="text-gray-500 text-sm">
                                No alternate sites added yet.
                            </p>
                        ) : (
                            <div className="space-y-4">
                                {alternateSites.map((site, index) => (
                                    <div
                                        key={index}
                                        className="border border-gray-300 rounded-lg p-4"
                                    >
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Site Name
                                                </label>
                                                <input
                                                    type="text"
                                                    value={site.siteName}
                                                    onChange={(e) =>
                                                        handleAlternateSiteChange(
                                                            index,
                                                            "siteName",
                                                            e.target.value
                                                        )
                                                    }
                                                    disabled={isViewMode}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Site Type
                                                </label>
                                                <select
                                                    value={site.siteType}
                                                    onChange={(e) =>
                                                        handleAlternateSiteChange(
                                                            index,
                                                            "siteType",
                                                            e.target.value
                                                        )
                                                    }
                                                    disabled={isViewMode}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
                                                >
                                                    <option value="Hot">Hot Site</option>
                                                    <option value="Warm">Warm Site</option>
                                                    <option value="Cold">Cold Site</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Address
                                                </label>
                                                <input
                                                    type="text"
                                                    value={site.address}
                                                    onChange={(e) =>
                                                        handleAlternateSiteChange(
                                                            index,
                                                            "address",
                                                            e.target.value
                                                        )
                                                    }
                                                    disabled={isViewMode}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Capacity (people)
                                                </label>
                                                <input
                                                    type="number"
                                                    value={site.capacity}
                                                    onChange={(e) =>
                                                        handleAlternateSiteChange(
                                                            index,
                                                            "capacity",
                                                            parseInt(e.target.value) || 0
                                                        )
                                                    }
                                                    disabled={isViewMode}
                                                    min="0"
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
                                                />
                                            </div>
                                        </div>
                                        {!isViewMode && (
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveAlternateSite(index)}
                                                className="mt-2 text-red-500 hover:text-red-700 text-sm"
                                            >
                                                Remove
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* IT Systems */}
                    <div className="mb-6">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-lg font-semibold text-gray-800">
                                IT Systems
                            </h2>
                            {!isViewMode && (
                                <button
                                    type="button"
                                    onClick={handleAddITSystem}
                                    className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600"
                                >
                                    + Add System
                                </button>
                            )}
                        </div>
                        {itSystems.length === 0 ? (
                            <p className="text-gray-500 text-sm">
                                No IT systems added yet.
                            </p>
                        ) : (
                            <div className="space-y-4">
                                {itSystems.map((system, index) => (
                                    <div
                                        key={index}
                                        className="border border-gray-300 rounded-lg p-4"
                                    >
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    System Name
                                                </label>
                                                <input
                                                    type="text"
                                                    value={system.systemName}
                                                    onChange={(e) =>
                                                        handleITSystemChange(
                                                            index,
                                                            "systemName",
                                                            e.target.value
                                                        )
                                                    }
                                                    disabled={isViewMode}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Criticality
                                                </label>
                                                <select
                                                    value={system.criticality}
                                                    onChange={(e) =>
                                                        handleITSystemChange(
                                                            index,
                                                            "criticality",
                                                            e.target.value
                                                        )
                                                    }
                                                    disabled={isViewMode}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
                                                >
                                                    <option value="Critical">Critical</option>
                                                    <option value="High">High</option>
                                                    <option value="Medium">Medium</option>
                                                    <option value="Low">Low</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Backup Location
                                                </label>
                                                <input
                                                    type="text"
                                                    value={system.backupLocation}
                                                    onChange={(e) =>
                                                        handleITSystemChange(
                                                            index,
                                                            "backupLocation",
                                                            e.target.value
                                                        )
                                                    }
                                                    disabled={isViewMode}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
                                                />
                                            </div>
                                            <div className="md:col-span-2">
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Recovery Procedure
                                                </label>
                                                <textarea
                                                    value={system.recoveryProcedure}
                                                    onChange={(e) =>
                                                        handleITSystemChange(
                                                            index,
                                                            "recoveryProcedure",
                                                            e.target.value
                                                        )
                                                    }
                                                    disabled={isViewMode}
                                                    rows={2}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
                                                />
                                            </div>
                                        </div>
                                        {!isViewMode && (
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveITSystem(index)}
                                                className="mt-2 text-red-500 hover:text-red-700 text-sm"
                                            >
                                                Remove
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Form Actions */}
                    <div className="flex justify-end gap-4 mt-6">
                        <button
                            type="button"
                            onClick={handleCancel}
                            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
                        >
                            {isViewMode ? "Back" : "Cancel"}
                        </button>
                        {!isViewMode && (
                            <button
                                type="submit"
                                disabled={loading}
                                className="px-6 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 disabled:bg-gray-400"
                            >
                                {loading
                                    ? "Saving..."
                                    : isEditMode
                                        ? "Update Plan"
                                        : "Create Plan"}
                            </button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddEditBCPPlan;
